import { useMutation } from '@apollo/client/react'
import { Checkbox, Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  type Customer,
  DocumentCaptureCheck,
  FindCustomerDocument,
  SetCustomerCaptureSettingsDocument,
} from '~app/graph/generated/graphql'
import useSectionForm from '~app/hooks/useSectionForm'
import SectionCard from './SectionCard'

/*
 * Component
 */

interface Props {
  customer: Pick<Customer, 'captureSettings' | 'id'>
  onStateChange: (args: { isDirty: boolean; isSaving: boolean }) => void
}

const CaptureSettingsSection = ({ customer, onStateChange }: Props) => {
  const { t } = useTranslation()
  const [setCustomerCaptureSettings] = useMutation(
    SetCustomerCaptureSettingsDocument
  )

  const { isDirty, isLoading, reset, submit, updateValues, values } =
    useSectionForm({
      errorMessage: t('customerDetails.notifications.saveFailed'),
      onStateChange,
      serverValues: {
        automaticTransferEnabled:
          customer.captureSettings.automaticTransferEnabled,
        disabledChecks: customer.captureSettings.disabledChecks,
      },
      successMessage: t('customerDetails.notifications.captureSettingsSaved'),
      onSubmit: async ({ values }) => {
        const { data } = await setCustomerCaptureSettings({
          // setCustomerCaptureSettings returns CustomerCaptureSettings, which
          // carries no id, so Apollo cannot normalise it into the cached
          // customer — the query has to be refetched instead.
          refetchQueries: [FindCustomerDocument],
          variables: { data: values, id: customer.id },
        })
        return data
          ? {
              automaticTransferEnabled:
                data.setCustomerCaptureSettings.automaticTransferEnabled,
              disabledChecks: data.setCustomerCaptureSettings.disabledChecks,
            }
          : undefined
      },
    })

  return (
    <SectionCard
      isDirty={isDirty}
      isLoading={isLoading}
      onDiscard={reset}
      onSave={submit}
      rows={[
        {
          content: (
            <Switch
              checked={values.automaticTransferEnabled}
              disabled={isLoading}
              onChange={automaticTransferEnabled =>
                updateValues({ automaticTransferEnabled })
              }
            />
          ),
          label: t('customerDetails.fields.automaticTransfer'),
        },
        {
          content: (
            <Checkbox.Group
              options={[
                {
                  label: t('customerDetails.captureChecks.customerNames'),
                  value: DocumentCaptureCheck.CustomerNamesAll,
                },
                {
                  label: t('customerDetails.captureChecks.publicId'),
                  value: DocumentCaptureCheck.PublicIdAll,
                },
              ]}
              value={values.disabledChecks}
              disabled={isLoading}
              onChange={disabledChecks =>
                updateValues({
                  disabledChecks: disabledChecks as DocumentCaptureCheck[],
                })
              }
            />
          ),
          label: t('customerDetails.fields.disabledChecks'),
        },
      ]}
      title={t('customerDetails.sections.captureSettings')}
    />
  )
}

export default CaptureSettingsSection
